/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import React, { useCallback, useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import { FullScreen } from '@chiragrupani/fullscreen-react';
import { VideoControls, OnHoldMessage } from 'components/modules';
import { NetworkStrength, VideoRoomParticipant, LocalVideoRoomParticipant, VideoStream } from 'components/elements';
import { useParticipants, useDominantSpeaker, useScreenShareParticipant, useVideoContext, useOnHoldStatus } from 'hooks';
import useControls from './useControls';

import Styles from './styles';

const useStyles = makeStyles(Styles);

function VideoRoom({ expectedParticipants, onToggleMobileChat, oid }) {
  const classes = useStyles();
  const [isFullScreenMode, setFullScreenMode] = useState(false);
  const onToggleFullScreenMode = () => setFullScreenMode(!isFullScreenMode);
  const { room } = useVideoContext();
  const participants = useParticipants();
  const dominantSpeaker = useDominantSpeaker();
  const { screenTrack, isSharingScreen, screenShareParticipant } = useScreenShareParticipant();

  const localParticipant = room?.localParticipant;
  const getParticipantName = useCallback((id) => expectedParticipants.filter(({ id: expId }) => id === expId)[0]?.name, [expectedParticipants]);
  const getIsProvider = useCallback((id) => expectedParticipants.filter(({ id: expId }) => id === expId)[0]?.role?.includes('provider'), [expectedParticipants]);
  const isProviderOnCall = expectedParticipants.some((participant) => participant?.role?.includes('provider') && participant?.callStatus);
  const localParticipantName = getParticipantName(localParticipant?.identity);
  const screenSharingParticipantName = getParticipantName(screenShareParticipant?.identity);
  const participantsCount = participants.length;

  const { isVisibleControls, showControls, hideControls } = useControls();

  const gridParticipantVideoItemStyle = cn(classes.gridParticipantVideoItem, { [classes.smallGridParticipantVideoItem]: participantsCount > 5 });
  const rightFeedParticipantVideoItemStyle = cn(classes.rightFeedParticipantVideoItem, { [classes.smallRightFeedParticipantVideoItem]: participantsCount > 5 });

  const { isCallOnHold } = useOnHoldStatus();
  const showOnHoldMessage = isCallOnHold || !isProviderOnCall;

  return (
    <FullScreen isFullScreen={isFullScreenMode} onChange={(isFull) => setFullScreenMode(isFull)}>
      <div className={classes.session}>
        <div className={classes.videoFeedWrapper} onClick={showControls} onMouseLeave={hideControls} onMouseOver={showControls}>
          {participantsCount === 0 && localParticipant && !isSharingScreen && !showOnHoldMessage && (
            <div className={classes.fullLocalParticipantVideoItem}>
              <NetworkStrength />
              <LocalVideoRoomParticipant
                participant={localParticipant}
                muted
                isDominant={localParticipant.identity === dominantSpeaker?.identity}
                nameLabel={localParticipantName}
                isLocalUser
                isScreenShared={isSharingScreen}
              />
            </div>
          )}

          {participantsCount === 1 && !isSharingScreen && !showOnHoldMessage && (
            <>
              {localParticipant && (
                <div className={classes.localParticipantVideoItem}>
                  <NetworkStrength />
                  <LocalVideoRoomParticipant
                    participant={localParticipant}
                    muted
                    isDominant={localParticipant.identity === dominantSpeaker?.identity}
                    nameLabel={localParticipantName}
                    isLocalUser
                    isScreenShared={isSharingScreen}
                  />
                </div>
              )}
              {participants.map((participant) => (
                <div className={classes.participantVideoItem} key={participant.identity}>
                  <VideoRoomParticipant
                    participant={participant}
                    isDominant={participant.identity === dominantSpeaker?.identity}
                    nameLabel={getParticipantName(participant.identity)}
                    isProvider={getIsProvider(participant.identity)}
                    isScreenShared={isSharingScreen}
                  />
                </div>
              ))}
            </>
          )}

          {participantsCount >= 2 && localParticipant && !isSharingScreen && !showOnHoldMessage && (
            <>
              {participants.map((participant) => (
                <div className={gridParticipantVideoItemStyle} key={participant.identity}>
                  <VideoRoomParticipant
                    participant={participant}
                    isDominant={participant.identity === dominantSpeaker?.identity}
                    nameLabel={getParticipantName(participant.identity)}
                    isProvider={getIsProvider(participant.identity)}
                    isScreenShared={isSharingScreen}
                  />
                </div>
              ))}
              <div className={gridParticipantVideoItemStyle}>
                <NetworkStrength />
                <LocalVideoRoomParticipant
                  participant={localParticipant}
                  muted
                  isDominant={localParticipant.identity === dominantSpeaker?.identity}
                  nameLabel={localParticipantName}
                  isLocalUser
                  isScreenShared={isSharingScreen}
                />
              </div>
            </>
          )}
          {showOnHoldMessage && (
            <>
              {localParticipant && (
                <div className={classes.localParticipantVideoItem}>
                  <NetworkStrength />
                  <LocalVideoRoomParticipant
                    participant={localParticipant}
                    muted
                    isDominant={localParticipant.identity === dominantSpeaker?.identity}
                    nameLabel={localParticipantName}
                    isLocalUser
                    isScreenShared={isSharingScreen}
                  />
                </div>
              )}
              <OnHoldMessage oid={oid} />
            </>
          )}
          {isSharingScreen && !showOnHoldMessage && (
            <>
              <div className={classes.shareParticipantVideoItem}>
                <VideoStream nameLabel={screenSharingParticipantName} videoTrack={screenTrack} />
              </div>
              <div className={classes.rightFeed}>
                <div className={rightFeedParticipantVideoItemStyle}>
                  <NetworkStrength />
                  <LocalVideoRoomParticipant
                    isSharingScreen
                    participant={localParticipant}
                    muted
                    isDominant={localParticipant.identity === dominantSpeaker?.identity}
                    nameLabel={localParticipantName}
                    isLocalUser
                    isScreenShared={isSharingScreen}
                  />
                </div>
                {participants.map((participant) => (
                  <div className={rightFeedParticipantVideoItemStyle} key={participant.identity}>
                    <VideoRoomParticipant
                      isSharingScreen
                      participant={participant}
                      isDominant={participant.identity === dominantSpeaker?.identity}
                      nameLabel={getParticipantName(participant.identity)}
                      isProvider={getIsProvider(participant.identity)}
                      isScreenShared={isSharingScreen}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
          <VideoControls
            isVisible={isVisibleControls || isFullScreenMode}
            onToggleFullScreenMode={onToggleFullScreenMode}
            isFullScreenMode={isFullScreenMode}
            onToggleMobileChat={onToggleMobileChat}
          />
        </div>
      </div>
    </FullScreen>
  );
}

VideoRoom.propTypes = {
  onToggleMobileChat: PropTypes.func.isRequired,
  expectedParticipants: PropTypes.arrayOf(PropTypes.any).isRequired,
  oid: PropTypes.string.isRequired,
};

export default VideoRoom;
